import { vPopper, UiPopper } from '@omnica/popper-vue2';
import Vue from 'vue';
import isEqual from 'lodash.isequal';
import { PLACEMENT } from '@omnica/popper';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
      t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function")
      for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
          if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
              t[p[i]] = s[p[i]];
      }
  return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
  var e = new Error(message);
  return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const normalizeCssClassesRecord = (classes) => {
    return Object.keys(classes).filter(k => classes[k]);
};
const normalizeCssClasses = (classes) => {
    const result = [];
    if (Array.isArray(classes)) {
        classes.forEach(classes => {
            if (typeof classes === 'string') {
                result.push(classes);
            }
            else {
                result.push(...normalizeCssClassesRecord(classes));
            }
        });
    }
    else {
        result.push(...normalizeCssClassesRecord(classes));
    }
    return result;
};

const arraify$1 = (value) => Array.isArray(value)
    ? [...value]
    : [value];
const render = (content, h) => arraify$1(typeof content === 'function'
    ? content(h)
    : content);

const arraify = (value) => Array.isArray(value)
    ? [...value]
    : [value];
const normalizeOptions = (optionsOrContent, modifiers) => {
    if (optionsOrContent === null) {
        return null;
    }
    const normalized = typeof optionsOrContent === 'object'
        ? optionsOrContent
        : { content: optionsOrContent };
    const { attrs, classes, centered, content, inline, large, style } = normalized, options = __rest(normalized, ["attrs", "classes", "centered", "content", "inline", "large", "style"]);
    if (!content) {
        return null;
    }
    return Object.assign({ classes: normalizeCssClasses([{
                'omnica-tooltip__popper': true,
                'omnica-tooltip__popper_centered': centered || Boolean(modifiers['centered']),
                'omnica-tooltip__popper_inline': inline || Boolean(modifiers['inline']),
                'omnica-tooltip__popper_large': large || Boolean(modifiers['large']),
            }, ...arraify(classes !== null && classes !== void 0 ? classes : {})]), content: (h) => h('div', {
            attrs: Object.assign(Object.assign({}, (attrs !== null && attrs !== void 0 ? attrs : {})), { style }),
            class: 'omnica-tooltip__content',
        }, render(content, h)), dark: !Boolean(modifiers['light']), delay: { show: 200, hide: 0 }, triggers: ['hover', 'focus', 'touch'] }, options);
};
const normalizeBinding = (binding) => {
    var _a, _b;
    return Object.assign(Object.assign({}, binding), { value: normalizeOptions((_a = binding.value) !== null && _a !== void 0 ? _a : null, binding.modifiers), oldValue: normalizeOptions((_b = binding.oldValue) !== null && _b !== void 0 ? _b : null, binding.modifiers) });
};
var vTooltip = {
    bind: (el, binding, node, prevVNode) => {
        vPopper.bind(el, normalizeBinding(binding), node, prevVNode);
    },
    update: (el, binding, node, prevVNode) => {
        // @ts-ignore
        if (!isEqual(binding.value, binding.oldValue, (a, b) => {
            return typeof a === 'function' && typeof b === 'function'
                ? a.toString() === b.toString()
                : undefined;
        })) {
            vPopper.update(el, normalizeBinding(binding), node, prevVNode);
        }
    },
    unbind: vPopper.unbind,
};

var OmnicaIconClearCircle = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"viewBox":"0 0 24 24","xmlns":"http://www.w3.org/2000/svg"}},[_c('path',{attrs:{"d":"M4 12a8 8 0 1116 0 8 8 0 01-16 0zm11 2.045c0-.122-.05-.24-.14-.324L13.148 12l1.712-1.721a.445.445 0 000-.648l-.49-.49a.444.444 0 00-.649 0L12 10.851 10.279 9.14a.444.444 0 00-.648 0l-.49.49a.444.444 0 000 .649L10.851 12 9.14 13.721a.444.444 0 000 .648l.49.49a.444.444 0 00.649 0L12 13.149l1.721 1.712a.444.444 0 00.648 0l.49-.49a.445.445 0 00.141-.325z","fill":"currentColor","fill-rule":"evenodd","clip-rule":"evenodd"}})]) } };

const toPx = (value) => value ? `${parseInt(String(value))}px` : 'none';
var script = Vue.extend({
    name: 'UiTooltip',
    components: {
        OmnicaIconClearCircle,
        UiPopper,
    },
    model: {
        prop: 'opened',
        event: 'toggle',
    },
    props: {
        /** Управлению видимостью контента подсказки вручную. Может использоваться с директивой v-model */
        opened: {
            type: Boolean,
            default: false,
        },
        /** События, при которых показывается подсказка. Например, 'hover' или 'focus' */
        trigger: {
            type: String,
            default: 'hover',
        },
        /** Позиционирование всплывающей подсказки */
        placement: {
            type: String,
            validator: (position) => Object.values(PLACEMENT).includes(position),
            default: PLACEMENT.BOTTOM,
        },
        /** Добавляет иконку закрытия к контенту подсказки и возможность закрыть её вручную */
        closable: {
            type: Boolean,
            default: false,
        },
        /** Центрирование текста для простого контента */
        centered: {
            type: Boolean,
            default: false,
        },
        /** Применяет к всплывающей подсказке темную тему */
        dark: {
            type: Boolean,
            default: false,
        },
        /** Изменение размера контента */
        large: {
            type: Boolean,
            default: false,
        },
        /**
         * Определяет наличие указателя.
         * Применяется по-умолчанию, если флаг closable выставлен в true.
         */
        withPointer: {
            type: Boolean,
            default: false,
        },
        /** Время в ms, через которое подсказка автоматически скроется */
        hideTime: {
            type: [Number, String],
            default: 0,
        },
        /** Однострочная, простая всплывающая подсказка */
        inline: {
            type: Boolean,
            default: false,
        },
        /** Задержка показать/скрыть, объект вида `{ show: 500, hide: 100 }` */
        delay: {
            type: [String, Number, Object],
            default: () => ({
                show: 200,
                hide: 0,
            }),
        },
        /** Заблокированный */
        disabled: {
            type: Boolean,
            default: false,
        },
        /** Контейнер, в который будет добавляться разметка контента */
        container: {
            type: null,
            validator: (value) => value === null || value instanceof Element || typeof value === 'string',
            default: null,
        },
        /** Максимальная ширина всплывающей подсказки */
        maxWidth: {
            type: [Number, String],
            default: null,
        },
        /** Минимальная высота всплывающей подсказки */
        minHeight: {
            type: [Number, String],
            default: null,
        },
        /** Определяет смещение всплывающей подсказки от целевого элемента в px */
        offset: {
            type: [Number, String],
            default: 8,
        },
        /** Классы для контейнера всплывающей подсказки */
        popperClass: {
            type: String,
            default: '',
        },
        /** Набор свойств плавающего элемента. See @omnica/popper-vue2 */
        popperOptions: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            shown: this.opened,
            hideTimeout: undefined,
        };
    },
    computed: {
        manual() {
            return this.trigger === 'manual' || !!this.hideTime || this.closable;
        },
        triggers() {
            return this.manual ? [] : [this.trigger];
        },
    },
    watch: {
        opened(opened) {
            this.shown = opened;
        },
    },
    beforeDestroy() {
        this.hide();
    },
    methods: {
        toPx,
        onContentClick(event) {
            const el = event.target;
            if (el.closest('[data-close-tooltip]')) {
                this.shown = false;
                /** Смена видимости всплывающей подсказки */
                this.$emit('toggle', false);
            }
        },
        show() {
            if (this.trigger === 'manual') {
                return;
            }
            const hideTime = parseInt(this.hideTime, 10) || 0;
            this.shown = true;
            this.$emit('toggle', true);
            if (hideTime) {
                clearTimeout(this.hideTimeout);
                this.hideTimeout = setTimeout(this.hide, this.hideTimeout);
            }
        },
        hide() {
            clearTimeout(this.hideTimeout);
            this.hideTimeout = undefined;
            this.shown = false;
        },
    },
});

function normalizeComponent(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier /* server only */, shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
    if (typeof shadowMode !== 'boolean') {
        createInjectorSSR = createInjector;
        createInjector = shadowMode;
        shadowMode = false;
    }
    // Vue.extend constructor export interop.
    const options = typeof script === 'function' ? script.options : script;
    // render functions
    if (template && template.render) {
        options.render = template.render;
        options.staticRenderFns = template.staticRenderFns;
        options._compiled = true;
        // functional template
        if (isFunctionalTemplate) {
            options.functional = true;
        }
    }
    // scopedId
    if (scopeId) {
        options._scopeId = scopeId;
    }
    let hook;
    if (moduleIdentifier) {
        // server build
        hook = function (context) {
            // 2.3 injection
            context =
                context || // cached call
                    (this.$vnode && this.$vnode.ssrContext) || // stateful
                    (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
            // 2.2 with runInNewContext: true
            if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
                context = __VUE_SSR_CONTEXT__;
            }
            // inject component styles
            if (style) {
                style.call(this, createInjectorSSR(context));
            }
            // register component module identifier for async chunk inference
            if (context && context._registeredComponents) {
                context._registeredComponents.add(moduleIdentifier);
            }
        };
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        options._ssrRegister = hook;
    }
    else if (style) {
        hook = shadowMode
            ? function (context) {
                style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot));
            }
            : function (context) {
                style.call(this, createInjector(context));
            };
    }
    if (hook) {
        if (options.functional) {
            // register for functional component in vue file
            const originalRender = options.render;
            options.render = function renderWithStyleInjection(h, context) {
                hook.call(context);
                return originalRender(h, context);
            };
        }
        else {
            // inject component registration as beforeCreate hook
            const existing = options.beforeCreate;
            options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
    }
    return script;
}

/* script */
const __vue_script__ = script;

/* template */
var __vue_render__ = function () {
  var _obj;
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "omnica-tooltip" },
    [
      _c(
        "div",
        {
          ref: "target",
          staticClass: "omnica-tooltip__trigger",
          on: { click: _vm.show },
        },
        [_vm._t("trigger")],
        2
      ),
      _vm._v(" "),
      _c(
        "UiPopper",
        _vm._b(
          {
            class:
              ((_obj = {
                "omnica-tooltip__popper": true,
                "omnica-tooltip__popper_centered": _vm.centered,
                "omnica-tooltip__popper_closable": _vm.closable,
                "omnica-tooltip__popper_inline": _vm.inline,
                "omnica-tooltip__popper_large": _vm.large,
                "omnica-tooltip__popper_pointer":
                  _vm.withPointer || _vm.closable,
                "omnica-tooltip__popper_with-steps":
                  _vm.$slots.steps && !_vm.$slots.title,
              }),
              (_obj[_vm.popperClass] = !!_vm.popperClass),
              _obj),
            attrs: {
              target: function () {
                return _vm.$refs.target
              },
              triggers: _vm.triggers,
              placement: _vm.placement,
              delay: _vm.delay,
              "offset-main-axis": _vm.offset,
              container: _vm.container,
              "auto-hide": !_vm.manual,
              dark: _vm.dark,
              "with-arrow": _vm.withPointer || _vm.closable,
              disabled: _vm.disabled,
            },
            on: {
              hide: function ($event) {
                return _vm.$emit("toggle", false)
              },
            },
            model: {
              value: _vm.shown,
              callback: function ($$v) {
                _vm.shown = $$v;
              },
              expression: "shown",
            },
          },
          "UiPopper",
          _vm.popperOptions,
          false
        ),
        [
          _c(
            "div",
            {
              staticClass: "omnica-tooltip__content",
              style: {
                maxWidth: _vm.toPx(_vm.maxWidth),
                minHeight: _vm.toPx(_vm.minHeight),
              },
              on: { click: _vm.onContentClick },
            },
            [
              _vm.closable
                ? _c(
                    "div",
                    {
                      staticClass: "omnica-tooltip__close",
                      on: {
                        click: function ($event) {
                          _vm.shown = false;
                        },
                      },
                    },
                    [_c("OmnicaIconClearCircle")],
                    1
                  )
                : _vm.$slots.steps
                ? _c(
                    "div",
                    { staticClass: "omnica-tooltip__steps" },
                    [_vm._t("steps")],
                    2
                  )
                : _vm._e(),
              _vm._v(" "),
              _vm.$slots.title
                ? _c(
                    "div",
                    {
                      class: {
                        "omnica-tooltip__title": true,
                        "omnica-tooltip__title_with-steps": _vm.$slots.steps,
                      },
                    },
                    [_vm._t("title")],
                    2
                  )
                : _vm._e(),
              _vm._v(" "),
              _vm._t("default"),
              _vm._v(" "),
              _vm.$slots.footer && _vm.large
                ? _c(
                    "div",
                    { staticClass: "omnica-tooltip__footer" },
                    [_vm._t("footer")],
                    2
                  )
                : _vm._e(),
            ],
            2
          ),
        ]
      ),
    ],
    1
  )
};
var __vue_staticRenderFns__ = [];
__vue_render__._withStripped = true;

  /* style */
  const __vue_inject_styles__ = undefined;
  /* scoped */
  const __vue_scope_id__ = undefined;
  /* module identifier */
  const __vue_module_identifier__ = undefined;
  /* functional template */
  const __vue_is_functional_template__ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  );

const OmnicaTooltipPlugin = {
    install(Vue) {
        Vue.directive('tooltip', vTooltip);
        Vue.component('UiTooltip', __vue_component__);
    },
};

export { OmnicaTooltipPlugin, __vue_component__ as UiTooltip, vTooltip };
